'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useApiClient } from '@/src/hooks/use-api-client';
import { GenerateLearnerLessonRequest, LearnerLesson } from '@/src/types/course';
import { Language } from '@/src/types/flashcard';
import { LanguageCombobox } from '@/src/components/language-combobox';
import { useLanguageStore } from '@/src/store/language-store';
import { Button } from '@/src/components/ui/button';
import { Badge } from '@/src/components/ui/badge';
import { Card, CardContent } from '@/src/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/src/components/ui/dialog';
import { Input } from '@/src/components/ui/input';
import { Label } from '@/src/components/ui/label';
import { Loader2, Search, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageTitle } from '@/src/components/page-title';

const SELECT_CLS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const LEVELS = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED'] as const;

export default function LearnerLessonsPage() {
  const router = useRouter();
  const apiClient = useApiClient();
  const { nativeLanguage, targetLanguage } = useLanguageStore();

  const [lessons, setLessons] = useState<LearnerLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');

  const [showGenerate, setShowGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState<GenerateLearnerLessonRequest>({
    prompt: '',
    level: 'BEGINNER',
    language: nativeLanguage,
    targetLanguage,
    nativeLanguage,
    withAudio: true,
    withImages: false,
    withQuiz: true
  });

  const [toDelete, setToDelete] = useState<LearnerLesson | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce the search box, and go back to the first page on a new query.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedQuery(query.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getLearnerLessons(page, 10, appliedQuery || undefined)
      .then((res) => {
        if (cancelled) return;
        setLessons(res?.data ?? []);
        setTotalPages(res?.totalPages ?? 0);
      })
      .catch(() => !cancelled && toast.error('Failed to load lessons'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [page, appliedQuery, reloadKey]);

  const handleGenerate = async () => {
    if (!form.prompt.trim()) return;
    try {
      setGenerating(true);
      const lesson = await apiClient.generateLearnerLesson({
        ...form,
        prompt: form.prompt.trim(),
        language: form.nativeLanguage
      });
      if (lesson?.id) {
        setShowGenerate(false);
        router.push(`/lessons/${lesson.id}`);
      } else {
        toast.error('Failed to generate lesson');
      }
    } catch {
      toast.error('Failed to generate lesson');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await apiClient.deleteLearnerLesson(toDelete.id);
      toast.success('Lesson deleted');
      setToDelete(null);
      setReloadKey((k) => k + 1);
    } catch {
      toast.error('Failed to delete lesson');
    } finally {
      setDeleting(false);
    }
  };

  const toggle = (key: 'withAudio' | 'withImages' | 'withQuiz', label: string) => (
    <label className='flex items-center gap-2 text-base'>
      <input
        type='checkbox'
        className='h-4 w-4'
        checked={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
      />
      {label}
    </label>
  );

  return (
    <>
      <PageTitle title='My Lessons - Ukata Learning' />
      <div className='container mx-auto max-w-4xl px-4 py-8'>
        <div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-2xl font-bold'>My Lessons</h1>
            <p className='text-muted-foreground'>Short lessons generated from your prompts.</p>
          </div>
          <Button onClick={() => setShowGenerate(true)}>
            <Sparkles className='mr-2 h-4 w-4' />
            Generate Lesson
          </Button>
        </div>

        <div className='relative mb-6'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            className='pl-9'
            placeholder='Search by title'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className='flex justify-center py-16'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        ) : lessons.length === 0 ? (
          <Card>
            <CardContent className='py-12 text-center text-muted-foreground'>
              {appliedQuery ? 'No lessons match your search.' : 'No lessons yet. Generate your first one.'}
            </CardContent>
          </Card>
        ) : (
          <div className='space-y-3'>
            {lessons.map((lesson) => (
              <Card
                key={lesson.id}
                className='cursor-pointer transition-colors hover:bg-muted/50'
                onClick={() => router.push(`/lessons/${lesson.id}`)}
              >
                <CardContent className='flex items-center justify-between gap-3 py-4'>
                  <div className='min-w-0'>
                    <p className='truncate font-semibold'>{lesson.title || 'Untitled lesson'}</p>
                    <div className='mt-1 flex items-center gap-2 text-sm text-muted-foreground'>
                      {lesson.createdDate && new Date(lesson.createdDate).toLocaleDateString()}
                      {lesson.generationStatus && lesson.generationStatus !== 'READY' && (
                        <Badge
                          variant={lesson.generationStatus === 'FAILED' ? 'destructive' : 'secondary'}
                        >
                          {lesson.generationStatus}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant='ghost'
                    size='icon'
                    aria-label='Delete lesson'
                    onClick={(e) => {
                      e.stopPropagation();
                      setToDelete(lesson);
                    }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className='mt-6 flex items-center justify-center gap-4'>
            <Button variant='outline' disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className='text-sm text-muted-foreground'>
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant='outline'
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}

        <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate a Lesson</DialogTitle>
              <DialogDescription>Describe what you want to learn.</DialogDescription>
            </DialogHeader>
            <div className='space-y-4 py-4'>
              <div className='space-y-2'>
                <Label>Prompt *</Label>
                <textarea
                  className='flex min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  placeholder='e.g., How to use は vs が'
                  maxLength={500}
                  value={form.prompt}
                  onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                />
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label>Learning Language</Label>
                  <LanguageCombobox
                    value={form.targetLanguage as Language}
                    onChange={(val) => setForm({ ...form, targetLanguage: val })}
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Native Language</Label>
                  <LanguageCombobox
                    value={form.nativeLanguage as Language}
                    onChange={(val) => setForm({ ...form, nativeLanguage: val })}
                  />
                </div>
              </div>
              <div className='space-y-2'>
                <Label>Level</Label>
                <select
                  className={SELECT_CLS}
                  value={form.level}
                  onChange={(e) =>
                    setForm({ ...form, level: e.target.value as GenerateLearnerLessonRequest['level'] })
                  }
                >
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l.charAt(0) + l.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className='flex flex-wrap gap-6'>
                {toggle('withAudio', 'Audio')}
                {toggle('withImages', 'Images')}
                {toggle('withQuiz', 'Quiz')}
              </div>
              <p className='text-sm text-muted-foreground'>
                Audio and images need a maker plan; without it the lesson is text only.
              </p>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setShowGenerate(false)} disabled={generating}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={generating || !form.prompt.trim()}>
                {generating ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Sparkles className='mr-2 h-4 w-4' />
                )}
                Generate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Lesson</DialogTitle>
              <DialogDescription>
                Delete &quot;{toDelete?.title || 'Untitled lesson'}&quot;? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant='outline' onClick={() => setToDelete(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button variant='destructive' onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
